import { useMemo } from 'react';
import type { JSX } from 'react';
import { useI18n } from '../../i18n';
import { deviceInfo } from '../../lib/deviceInfo';
import { Icon } from './Icon';
import { Module } from './Module';

/**
 * Small card summarising the device that will be reported to the backend —
 * a port of `android/.../ui/components/DeviceInfoCard.kt`.
 *
 * ### The icon is an iPhone, and that is a substitution
 *
 * Android draws `Icons.Outlined.PhoneAndroid`. The glyph is a phone with
 * Android's silhouette, and putting it on an iPhone would be a small permanent
 * lie in the corner of the one card whose entire job is to say what this device
 * is. `Icon.tsx` carries `'phone-iphone'` for exactly this call site.
 *
 * ### The summary line has no OS version, and never will
 *
 * Android's `device_summary` reads "Android 14 · v1.2.0": it fills the first
 * slot with the OS version because it can simply ask for it. Here that slot
 * carries `platform` — the literal `"ios-webclip"` the backend receives — for
 * two reasons. The OS version could only come from scraping
 * `"OS 17_2 like Mac OS X"` out of the user-agent, which is not a format Apple
 * has promised to keep and which produces a value that silently becomes wrong
 * (SPEC §8, and `buildMeta` refuses it for the same reason). And the model is
 * already the line above, so repeating it here would spend the row on nothing.
 * "iPhone" over "ios-webclip · v0.4.1" tells an operator the device class, the
 * app that is running and its build; that is what the Android card tells them
 * too.
 */
export function DeviceInfoCard({ className }: { className?: string }): JSX.Element {
  const { t } = useI18n();
  // Read on first render rather than at module scope: `deviceInfo()` defaults to
  // `navigator.userAgent`, and this module is importable from a
  // node-environment test where touching `navigator` at import time would take
  // the whole file down. Memoised because the card describes the one device it
  // is running on — that answer cannot change while the app is mounted, so
  // re-parsing the user-agent string on every render would be work for a value
  // that is already known.
  const device = useMemo(() => deviceInfo(), []);

  return (
    <Module className={className}>
      <div className="module__row">
        <Icon name="phone-iphone" size={24} className="device-card__icon" />
        <div className="device-card__body">
          {/*
            Weight comes from the type class (.t-title-small → 500), never from a
            local override. Android's comment is the reason: hand-setting a
            weight the bundled family does not instance does not fail, it
            silently synthesises a smeared face.
          */}
          <div className="device-card__model t-title-small clamp-2 break-anywhere">{device.model}</div>
          <div className="device-card__summary t-body-small clamp-2">
            {t('device_summary', device.platform, device.appVersion)}
          </div>
        </div>
      </div>
    </Module>
  );
}
